/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 */

/* ------------------------------------------------------------
   Cutest Bae Goals — Ambient Motion System (JS half)

   1. Injects the two fixed ambient background layers that give
      the store its continuously drifting "living" backdrop.
   2. Adds scroll-reveal + a light stagger to sections and
      product / collection cards site-wide.
   3. Flags the homepage hero for the headline sheen.

   Pairs with assets/motion.css. All motion is CSS-driven, so
   this file does no per-frame work — no scroll or rAF loops.
   ------------------------------------------------------------ */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function markVisible(el) {
    el.classList.add('is-visible');
  }

  /* --- 1. Ambient background layers ------------------------ */
  function buildLayer(className, orbCount) {
    var layer = document.createElement('div');
    layer.className = className;
    layer.setAttribute('aria-hidden', 'true');
    for (var i = 1; i <= orbCount; i++) {
      var orb = document.createElement('span');
      orb.className = 'cbg-orb cbg-orb--' + i;
      layer.appendChild(orb);
    }
    return layer;
  }

  function initAmbientBackground() {
    if (document.querySelector('.cbg-bg')) { return; }
    var body = document.body;
    if (!body) { return; }
    // Deep layer goes first in the DOM so it paints behind content.
    body.insertBefore(buildLayer('cbg-bg', 3), body.firstChild);
    // Veil layer is appended last; CSS lifts it above with a
    // soft-light blend at low opacity.
    body.appendChild(buildLayer('cbg-veil', 2));
  }

  /* --- 2. Scroll reveals ----------------------------------- */
  function initReveal() {
    var targets = [];

    // Every theme section shares this wrapper convention. Off-canvas
    // panels (sidebar menu, cart drawer, popup) also use the <section>
    // wrapper in some cases, but they're marked aria-hidden="true" by
    // the theme since they're meant to stay off-screen until opened —
    // excluding them here keeps our fade/slide transform from ever
    // fighting with the theme's own off-canvas transform.
    //
    // The homepage slideshow/hero is also excluded: it's the first
    // thing a visitor sees, and gating its visibility behind JS +
    // IntersectionObserver risks the headline staying in its
    // pre-reveal (faded/offset) state on slower mobile devices —
    // exactly the kind of "text doesn't show" bug above-the-fold
    // content should never be exposed to. The hero gets its own
    // guaranteed, JS-independent entrance animation in motion.css
    // instead (section 4).
    var sections = document.querySelectorAll('section[data-section-type]:not([aria-hidden="true"]):not([data-section-type="slideshow"])');
    Array.prototype.forEach.call(sections, function (section) {
      section.classList.add('cbg-reveal');
      targets.push(section);
    });

    // Product & collection cards get a light cascading stagger
    var cards = document.querySelectorAll('.ProductItem, .CollectionItem');
    Array.prototype.forEach.call(cards, function (card, index) {
      card.classList.add('cbg-reveal');
      card.style.transitionDelay = (Math.min(index % 6, 5) * 0.08) + 's';
      targets.push(card);
    });

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(markVisible);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          markVisible(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    targets.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      // Anything already in the viewport on load should just appear,
      // not wait on a scroll event that may never come.
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        markVisible(el);
      } else {
        observer.observe(el);
      }
    });
  }

  /* --- 3. Hero sheen --------------------------------------- */
  function initHeroSheen() {
    var heroContent = document.querySelector('[data-section-type="slideshow"] .Slideshow__Content');
    if (heroContent) {
      heroContent.classList.add('cbg-ambient');
    }
  }

  /* --- 4. Empty-cart "Continue Shopping" closes the cart ---
     Clicking it opens the nav menu drawer (data-action="open-drawer"
     data-drawer-id="sidebar-menu", set in cart-drawer.liquid), but
     the theme doesn't auto-close whichever drawer was already open.
     Rather than guess at the theme's internal drawer state/classes,
     this reuses the cart drawer's own close button by triggering a
     real click on it — the exact same path a shopper clicking the
     X would take — so the cart closes in sync with the menu opening. */
  function initContinueShoppingClosesCart() {
    document.addEventListener('click', function (event) {
      var continueBtn = event.target.closest('.Cart__ContinueShopping');
      if (!continueBtn) { return; }

      var cartCloseBtn = document.querySelector('#sidebar-cart [data-action="close-drawer"][data-drawer-id="sidebar-cart"]');
      if (cartCloseBtn) {
        cartCloseBtn.click();
      }
    });
  }

  /* --- 5. Click outside an open drawer closes it -----------
     Covers both the cart drawer and the nav menu drawer (they
     share the theme's .Drawer base class and both start
     aria-hidden="true", flipping to "false" when opened — same
     pattern already relied on above). A click anywhere that
     isn't inside the currently open drawer, and isn't the
     control that opened it, triggers that drawer's own close
     button — the same click path as pressing its "X" — so we
     never have to guess at or duplicate the theme's internal
     open/close logic. */
  function initOutsideClickClosesDrawer() {
    document.addEventListener('click', function (event) {
      // Let a click that's opening a drawer run its own logic
      // undisturbed, even if another drawer happens to be open.
      if (event.target.closest('[data-action="open-drawer"]')) { return; }

      var openDrawers = document.querySelectorAll('.Drawer[aria-hidden="false"]');
      if (!openDrawers.length) { return; }

      Array.prototype.forEach.call(openDrawers, function (drawer) {
        // Click landed inside this drawer (a link, a menu item, the
        // close button itself) — leave it to behave normally.
        if (drawer.contains(event.target)) { return; }

        var closeBtn = drawer.querySelector('[data-action="close-drawer"]');
        if (closeBtn) {
          closeBtn.click();
        }
      });
    });
  }

  /* --- 6. Floating hearts & sparkle glow --------------------
     A third ambient layer, site-wide — same z-index band as the
     .cbg-bg wine background (see motion.css section 14), so it
     shows through the header, footer, and every page exactly the
     way the orbs in section 1 already do. This function only
     builds the markup; the motion is section 7. */
  function initHeartsAndSparkles() {
    if (document.querySelector('.cbg-hearts')) { return; }

    var layer = document.createElement('div');
    layer.className = 'cbg-hearts';
    layer.setAttribute('aria-hidden', 'true');

    for (var i = 1; i <= 6; i++) {
      var heart = document.createElement('span');
      heart.className = 'cbg-heart cbg-heart--' + i;
      layer.appendChild(heart);
    }

    for (var j = 1; j <= 8; j++) {
      var sparkle = document.createElement('span');
      sparkle.className = 'cbg-sparkle cbg-sparkle--' + j;
      layer.appendChild(sparkle);
    }

    document.body.appendChild(layer);
  }

  /* --- 7. Drift field (continuous motion + swipe response) ---
     Replaces the CSS keyframe motion these shapes used to have.
     A keyframe loop always has a last frame, and repeating it
     snaps back to the first — which is what read as the shapes
     "resetting" every few seconds. Here each shape carries its
     own velocity, gets nudged along once per frame, and wraps
     around the viewport edges, so there is no cycle to restart:
     it simply keeps going.

     Scrolling and swiping feed an impulse into the whole field.
     Swipe up and the shapes drift up with you, then ease back
     into their own slow drift over about a second. Each shape has
     its own `depth`, so nearer ones react more than far ones and
     the field reads as having volume rather than moving as one
     flat sheet.

     Perf notes, since this is the one piece of per-frame work on
     the site: the loop only ever writes `transform`, which the
     browser resolves on the compositor without re-laying out or
     repainting the page; it never reads geometry back (no layout
     thrash); the scroll and touch listeners are passive so they
     can't delay scrolling; and the loop stops itself entirely
     when the tab is hidden or the visitor prefers reduced
     motion. */
  function initDriftField() {
    if (prefersReducedMotion) { return; }

    var layer = document.querySelector('.cbg-hearts');
    if (!layer) { return; }

    var nodes = layer.querySelectorAll('.cbg-heart, .cbg-sparkle');
    if (!nodes.length) { return; }

    var MARGIN = 80;          // wrap this far outside the viewport
    var IMPULSE_CAP = 26;     // keeps a hard fling from flinging shapes
    var DAMPING = 0.92;       // impulse decays to ~nothing in about a second

    var viewW = window.innerWidth;
    var viewH = window.innerHeight;
    var shapes = [];
    var impulseX = 0;
    var impulseY = 0;
    var lastScrollY = window.pageYOffset;
    var touchX = null;
    var running = false;
    var frameId = null;

    function rand(min, max) {
      return min + Math.random() * (max - min);
    }

    function place(shape) {
      shape.x = rand(-MARGIN, viewW + MARGIN);
      shape.y = rand(-MARGIN, viewH + MARGIN);
    }

    Array.prototype.forEach.call(nodes, function (node) {
      var isHeart = node.classList.contains('cbg-heart');

      // Hand position over to transform entirely. The CSS top/left
      // values stay in the stylesheet as the no-JS fallback, but
      // once we're driving things they'd fight with the transform.
      node.style.top = '0';
      node.style.left = '0';

      var shape = {
        node: node,
        isHeart: isHeart,
        // Slow base drift, in px per frame at 60fps. Every shape
        // gets its own so the field never moves in lockstep.
        vx: rand(-0.22, 0.22),
        vy: rand(-0.30, -0.06),
        // How strongly this shape answers a swipe. Small shapes
        // sit "further away" and move less.
        depth: rand(0.35, 1),
        rot: isHeart ? -45 : 0,
        rotSpeed: isHeart ? rand(-0.06, 0.06) : 0
      };

      place(shape);

      if (!isHeart) {
        // Give every sparkle its own rhythm, then drop it somewhere
        // new at the end of each twinkle. The keyframes sit at zero
        // opacity at both ends of the cycle, so the jump lands while
        // the sparkle is invisible and simply reads as a new sparkle
        // catching the light somewhere else.
        node.style.animationDuration = rand(2.6, 5.2).toFixed(2) + 's';
        node.style.animationDelay = rand(0, 4).toFixed(2) + 's';
        node.addEventListener('animationiteration', function () {
          place(shape);
        });
      }

      shapes.push(shape);
    });

    function wrap(shape) {
      if (shape.x < -MARGIN) { shape.x = viewW + MARGIN; }
      else if (shape.x > viewW + MARGIN) { shape.x = -MARGIN; }

      if (shape.y < -MARGIN) { shape.y = viewH + MARGIN; }
      else if (shape.y > viewH + MARGIN) { shape.y = -MARGIN; }
    }

    function frame() {
      impulseX *= DAMPING;
      impulseY *= DAMPING;

      for (var i = 0; i < shapes.length; i++) {
        var shape = shapes[i];

        shape.x += shape.vx + impulseX * shape.depth;
        shape.y += shape.vy + impulseY * shape.depth;
        wrap(shape);

        var transform = 'translate3d(' + shape.x.toFixed(1) + 'px,' +
                        shape.y.toFixed(1) + 'px,0)';

        if (shape.isHeart) {
          shape.rot += shape.rotSpeed;
          transform += ' rotate(' + shape.rot.toFixed(1) + 'deg)';
        }

        shape.node.style.transform = transform;
      }

      frameId = window.requestAnimationFrame(frame);
    }

    function start() {
      if (running) { return; }
      running = true;
      frameId = window.requestAnimationFrame(frame);
    }

    function stop() {
      if (!running) { return; }
      running = false;
      window.cancelAnimationFrame(frameId);
    }

    function addImpulse(dx, dy) {
      impulseX += dx;
      impulseY += dy;
      if (impulseX > IMPULSE_CAP) { impulseX = IMPULSE_CAP; }
      if (impulseX < -IMPULSE_CAP) { impulseX = -IMPULSE_CAP; }
      if (impulseY > IMPULSE_CAP) { impulseY = IMPULSE_CAP; }
      if (impulseY < -IMPULSE_CAP) { impulseY = -IMPULSE_CAP; }
    }

    // Vertical: derived from scroll, so it covers mouse wheel,
    // trackpad and touch alike. Scrolling down the page means the
    // finger travelled up, so the shapes travel up too.
    window.addEventListener('scroll', function () {
      var y = window.pageYOffset;
      addImpulse(0, -(y - lastScrollY) * 0.32);
      lastScrollY = y;
    }, { passive: true });

    // Horizontal: scroll can't tell us about a sideways swipe, so
    // read it off the touch directly.
    window.addEventListener('touchstart', function (event) {
      touchX = event.touches[0].clientX;
    }, { passive: true });

    window.addEventListener('touchmove', function (event) {
      if (touchX === null) { return; }
      var x = event.touches[0].clientX;
      addImpulse((x - touchX) * 0.10, 0);
      touchX = x;
    }, { passive: true });

    window.addEventListener('touchend', function () {
      touchX = null;
    }, { passive: true });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        viewW = window.innerWidth;
        viewH = window.innerHeight;
      }, 150);
    });

    // No reason to burn battery animating a tab nobody is looking at.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else {
        lastScrollY = window.pageYOffset;
        start();
      }
    });

    start();
  }

  function init() {
    initAmbientBackground();
    initReveal();
    initHeroSheen();
    initContinueShoppingClosesCart();
    initOutsideClickClosesDrawer();
    initHeartsAndSparkles();
    initDriftField();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
